import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { IMAGE_URL } from '../constants/Consts';
import {
  getAboutSections,
  createAboutSection,
  updateAboutSection,
  deleteAboutSection
} from './_request';

const defaultForm = {
  section_key: '',
  section_title: '',
  section_content: '',
  image: '',
  ordering: 0,
  active: false,
};

const AboutSections = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [filterKey, setFilterKey] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  const fetchSections = async () => {
    setLoading(true);
    try {
      const params = filterKey ? { section_key: filterKey } : {};
      const res = await getAboutSections(params);
      setSections(res.data || []);
    } catch (err) {
      setError('Failed to fetch sections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSections();
    // eslint-disable-next-line
  }, [filterKey]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setForm((prev) => ({ ...prev, image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleEdit = (section) => {
    setForm({ ...section, active: !!section.active });
    setEditingId(section.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    setLoading(true);
    try {
      await deleteAboutSection(id);
      fetchSections();
    } catch (err) {
      setError('Failed to delete section');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (editingId) {
        await updateAboutSection(editingId, form);
      } else {
        await createAboutSection(form);
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchSections();
    } catch (err) {
      setError('Failed to save section');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">About / Intro / Taaruf Sections</h2>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddNew}>
          Add New
        </Button>
      </div>
      <div className="mb-4">
        <TextField
          label="Filter by Section Key (e.g. noor_diary, taaruf)"
          value={filterKey}
          onChange={(e) => setFilterKey(e.target.value)}
          size="small"
        />
      </div>
      {loading ? (
        <div className="flex justify-center"><CircularProgress /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 border">Section Key</th>
                <th className="p-2 border">Title</th>
                <th className="p-2 border">Active</th>
                <th className="p-2 border">Image</th>
                <th className="p-2 border">Order</th>
                <th className="p-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((section) => (
                <tr key={section.id} className={section.active ? 'bg-green-50' : ''}>
                  <td className="p-2 border">{section.section_key}</td>
                  <td className="p-2 border">{section.section_title}</td>
                  <td className="p-2 border text-center">{section.active ? '✅' : ''}</td>
                  <td className="p-2 border text-center">
                    {section.image && (
                      <img src={section.image.startsWith('data:') ? section.image : `${IMAGE_URL}api/about/aboutImage/${section.image}`} alt="" className="w-12 h-12 object-cover mx-auto" />
                    )}
                  </td>
                  <td className="p-2 border text-center">{section.ordering}</td>
                  <td className="p-2 border text-center">
                    <IconButton onClick={() => handleEdit(section)}><EditIcon /></IconButton>
                    <IconButton onClick={() => handleDelete(section.id)}><DeleteIcon /></IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && (
        <form className="mt-8 max-w-xl mx-auto bg-white p-6 rounded shadow" onSubmit={handleSubmit}>
          <h3 className="text-xl font-semibold mb-4">{editingId ? 'Edit' : 'Add'} Section</h3>
          <div className="mb-4">
            <TextField
              label="Section Key (e.g. noor_diary, taaruf)"
              name="section_key"
              value={form.section_key}
              onChange={handleInputChange}
              fullWidth
              required
            />
          </div>
          <div className="mb-4">
            <TextField
              label="Title"
              name="section_title"
              value={form.section_title}
              onChange={handleInputChange}
              fullWidth
              required
            />
          </div>
          <div className="mb-4">
            <TextField
              label="Content"
              name="section_content"
              value={form.section_content}
              onChange={handleInputChange}
              fullWidth
              multiline
              minRows={3}
              required
            />
          </div>
          <div className="mb-4">
            <input type="file" accept="image/*" onChange={handleImageChange} />
            {form.image && (
              <img src={form.image.startsWith('data:') ? form.image : `${IMAGE_URL}api/about/aboutImage/${form.image}`} alt="" className="w-24 h-24 object-cover mt-2" />
            )}
          </div>
          <div className="mb-4">
            <TextField
              label="Order"
              name="ordering"
              type="number"
              value={form.ordering}
              onChange={handleInputChange}
              fullWidth
            />
          </div>
          <div className="mb-4">
            <FormControlLabel
              control={<Switch checked={!!form.active} onChange={handleInputChange} name="active" color="primary" />}
              label="Set as Active"
            />
          </div>
          {error && <div className="text-red-500 mb-2">{error}</div>}
          <div className="flex gap-4">
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {editingId ? 'Update' : 'Create'}
            </Button>
            <Button variant="outlined" onClick={() => { setShowForm(false); setEditingId(null); setForm(defaultForm); }}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AboutSections; 